import { UserGroup } from '@mews-commander/data-access';
import { UnsavedChangesDialog } from '@mews-commander/ui';
import { LocalizedText, showSuccessAlert, useDialog } from '@mews/framework';
import { FloatingPanel } from '@mews/b2b-ui';
import { TextKey } from '@mews-commander/enums';
import { Button } from '@optimus-web/core';
import { useGroupSettingsForm } from './useGroupSettingsForm';
import { GroupSettingsForm } from './GroupSettingsForm';

export const GroupSettings = ({
    group,
    active,
    onClose,
}: {
    group: UserGroup;
    active: boolean;
    onClose: () => void;
}) => {
    const [{ active: showUnsavedChangesDialog }, toggleUnsavedChangesDialog] = useDialog();

    const { renderFormContent, submit, isPending, hasDirtyFields, reset } = useGroupSettingsForm({
        group,
        onSuccess: () => {
            reset();
            onClose();

            showSuccessAlert({
                title: <LocalizedText textKey={TextKey.ChangesSaved} />,
                description: (
                    <LocalizedText textKey={TextKey.UserGroupDetailsEdited} substitutes={{ groupName: group.name }} />
                ),
            });
        },
    });

    const handleClose = () => {
        if (hasDirtyFields) {
            toggleUnsavedChangesDialog();
        } else {
            onClose();
        }
    };

    return renderFormContent(
        <>
            <FloatingPanel
                active={active}
                onClose={handleClose}
                title={<LocalizedText textKey={TextKey.EditGroupSettings} substitutes={{ groupName: group.name }} />}
                loading={isPending}
                footer={{
                    secondary: (
                        <Button onClick={handleClose} type="button">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                    primary: (
                        <Button variant="primary" onClick={submit} type="submit">
                            <LocalizedText textKey={TextKey.Save} />
                        </Button>
                    ),
                }}
            >
                <GroupSettingsForm group={group} />
            </FloatingPanel>
            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                content={
                    <LocalizedText
                        textKey={TextKey.UserEditUnsavedChangesDescription}
                        substitutes={{ name: group.name }}
                    />
                }
                onCancel={toggleUnsavedChangesDialog}
                onDiscardChanges={() => {
                    onClose();
                    reset();
                    toggleUnsavedChangesDialog();
                }}
            />
        </>
    );
};
